package com.kaihuoy.web

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.renderComposable
import com.kaihuoy.auth.LoginScreen
import com.kaihuoy.web.router.AppRouter // Import the AppRouter
import com.kaihuoy.shared.data.User
import com.kaihuoy.components.common.*
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch

// Utility function for Service Worker registration
fun registerServiceWorker() {
    @Suppress("ALWAYS_NULL")
    if (window.navigator.serviceWorker != null) {
        window.navigator.serviceWorker.register("/service-worker.js?v=9")
            .then({ registration ->
                println("Service Worker registered with scope: ${registration.asDynamic().scope}")
                
                // Automatically check for updates
                registration.asDynamic().update()
                null
            }, { error ->
                println("Service Worker registration failed: $error")
                null
            })

        // Reload the page when a new service worker takes over
        var refreshing = false
        window.navigator.serviceWorker.addEventListener("controllerchange", {
            if (!refreshing) {
                refreshing = true
                window.location.reload()
            }
        })
    } else {
        println("Service Workers are not supported by this browser.")
    }
}

sealed class Screen(val route: String) {
    object Loading : Screen("loading")
    object Login : Screen("login")
    object Home : Screen("home")
    // Add other screens as needed
}

private val scope = MainScope()

fun main() {
    document.getElementById("app-loading")?.classList?.add("hidden")
    renderComposable(rootElementId = "root") {
        Style(KaihuStyles)
        var currentScreen by remember { mutableStateOf<Screen>(Screen.Loading) }
        var user by remember { mutableStateOf<User?>(null) }
        var isAdmin by remember { mutableStateOf(false) } // Manage isAdmin here

        LaunchedEffect(Unit) {
            registerServiceWorker()
        }

        // Listen for auth state changes
        LaunchedEffect(Unit) {
            FirebaseService.auth.authStateChanged.collect { firebaseUser ->
                if (firebaseUser != null) {
                    user = User(
                        uid = firebaseUser.uid,
                        email = firebaseUser.email ?: "",
                        displayName = firebaseUser.displayName ?: "Käyttäjä",
                        isAdmin = false // Default to false, check below
                    )
                    // If user is logged in, navigate to Home
                    currentScreen = Screen.Home

                    // Haetaan käyttäjän omat tiedot (sisältää isAdmin ja isHuolto)
                    scope.launch {
                        try {
                            FirebaseService.database.reference("users").child(firebaseUser.uid).valueEvents.collect { snapshot ->
                                val userData = snapshot.value<User>()
                                isAdmin = userData.isAdmin
                                user = userData.copy(
                                    email = userData.email.ifBlank { firebaseUser.email ?: "" },
                                    displayName = userData.displayName.ifBlank { firebaseUser.displayName ?: "Käyttäjä" }
                                )
                            }
                        } catch (e: kotlinx.coroutines.CancellationException) {
                            // Normaali peruutus
                        } catch (e: Exception) {
                            println("Käyttäjätietojen haku epäonnistui: ${e.message}")
                        }
                    }
                } else {
                    user = null
                    isAdmin = false // Reset admin status on logout
                    // If user logs out, navigate to Login
                    currentScreen = Screen.Login
                }
            }
        }

        Div({ classes("container") }) {
            when (currentScreen) {
                Screen.Loading -> {}
                Screen.Login -> LoginScreen(
                    onLoginSuccess = { currentScreen = Screen.Home }
                )
                Screen.Home -> AppRouter(
                    user = user,
                    isAdmin = isAdmin,
                    onLogout = { scope.launch { FirebaseService.auth.signOut() } }
                )
            }
        }

        AppFeedbackHost()
    }
}


